import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Spin,
  Switch,
  Table,
  Popconfirm,
  Space,
} from "antd";
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";
import { useHistory, useLocation } from "react-router-dom";
import { CheckOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";

import { Locale } from "../../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../../utils/helpers/helpers";
import { merchantHasCommission } from "../../../utils/helpers/merchantHelpers";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import { getMerchantSettings } from "../../../utils/redux/reducers/Merchant";
import { BreadCrumb } from "../../../components/main/LayoutComponents";

import "./AddOrEditAlbum.scss";
import {
  create,
  getOne,
  update,
} from "../../../utils/http/dataProvider";
import { syncCoursesToAlbum } from "../../../utils/http/albumDataProvider";
import ImageUpload from "../../../components/media/ImageUpload";
import TranslateBtn from "../../../components/product/TranslateBtn";
import SlateRichTextEditor from "../../../components/editor/SlateRichTextEditor";
import LinkSelector from "../../../components/linkSelector/LinkSelector";
import CourseSelectionModal from "./CourseSelectionModal";
import AlbumCommissionForm from "./AlbumCommissionForm";

const i18n = Locale.getInstance();

const AddOrEditAlbum = () => {
  const user = useSelector((state) => getUserInfo(state));
  console.log("🚀 ~ AddOrEditAlbum ~ user:", user)
  const settings = useSelector((state) => getMerchantSettings(state));
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(false);
  const [showValidDays, setShowValidDays] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courseModalVisible, setCourseModalVisible] = useState(false);
  const [commissions, setCommissions] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const queryClient = useQueryClient();
  const [albumForm] = useForm();
  const history = useHistory();
  const location = useLocation();

  const id = location?.state?.id;

  const { data, isLoading } = useQuery(
    ["course-album", id, user.token],
    () => getOne("course/albums", { id, token: user.token }),
    {
      enabled: !!id,
    }
  );

  useEffect(() => {
    if (id && data?.id === id) {
      const values = {
        zh_name: data.zh_name,
        ug_name: data.ug_name,
        zh_description: data.zh_description,
        ug_description: data.ug_description,
        ug_cover: data?.ug_cover_media ? [data?.ug_cover_media] : [],
        zh_cover: data?.zh_cover_media ? [data?.zh_cover_media] : [],
        ug_portrait_cover: data?.ug_portrait_cover_media ? [data?.ug_portrait_cover_media] : [],
        zh_portrait_cover: data?.zh_portrait_cover_media ? [data?.zh_portrait_cover_media] : [],
        course_category_id: data.course_category_id,
        status: data.status,
        market_price: data.market_price,
        original_price: data.original_price,
        price: data.price,
        not_for_sale: !!data.not_for_sale,
        order: data.order,
        valid_days: data.valid_days,
        valid_forever: data.valid_forever,
      };
      if (data.category) {
        setCategory(data.category);
      }
      if (data.courses) {
        setSelectedCourses(data.courses);
      }
      if (data.valid_forever === 0) {
        setShowValidDays(true);
      }
      // Initialize commissions from album data
      if (data.commissions && Object.keys(data.commissions).length > 0) {
        const commissionArray = Object.keys(data.commissions).map((levelId) => ({
          id: data.commissions[levelId].agent_level_id,
          value: {
            commission: data.commissions[levelId].commission,
            parent_commission: data.commissions[levelId].parent_commission,
          },
        }));
        setCommissions(commissionArray);
        setSelectedLevels(commissionArray.map((c) => c.id));
      }
      albumForm.setFieldsValue(values);
    }
    if (!id) {
      const values = {
        zh_name: "",
        ug_name: "",
        zh_description: null,
        ug_description: null,
        ug_cover: [],
        zh_cover: [],
        ug_portrait_cover: [],
        zh_portrait_cover: [],
        course_category_id: "",
        status: 1,
        market_price: "",
        original_price: "",
        price: "",
        not_for_sale: false,
        order: "",
        valid_days: "",
        valid_forever: 1,
      };
      albumForm.setFieldsValue(values);
    }
  }, [data, id]);

  const addAlbumMutation = useMutation(create, {
    onSuccess: async (result) => {
      const albumId = result?.id;
      if (albumId && selectedCourses.length > 0) {
        const courseIds = selectedCourses.map((c) => c.id);
        try {
          await syncCoursesToAlbum(albumId, courseIds, user.token);
        } catch (error) {
          handleMutationError(error);
        }
      }
      queryClient.invalidateQueries("course-albums");
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setLoading(false);
      history.goBack();
    },
    onError: (error) => {
      handleMutationError(error);
      setLoading(false);
    },
  });

  const updateAlbumMutation = useMutation(update, {
    onSuccess: async () => {
      if (selectedCourses.length > 0) {
        const courseIds = selectedCourses.map((c) => c.id);
        try {
          await syncCoursesToAlbum(id, courseIds, user.token);
        } catch (error) {
          handleMutationError(error);
        }
      }
      queryClient.invalidateQueries("course-albums");
      queryClient.invalidateQueries(["course-album", id]);
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      setLoading(false);
      history.goBack();
    },
    onError: (error) => {
      handleMutationError(error);
      setLoading(false);
    },
  });

  const onFinish = (values) => {
    if (
      values.zh_cover &&
      values.zh_cover.length > 0 &&
      values.zh_cover[0].path
    ) {
      values["zh_cover"] = values["zh_cover"][0]["path"];
    }
    if (
      values.ug_cover &&
      values.ug_cover.length > 0 &&
      values.ug_cover[0].path
    ) {
      values["ug_cover"] = values["ug_cover"][0]["path"];
    }
    if (
      values.zh_portrait_cover &&
      values.zh_portrait_cover.length > 0 &&
      values.zh_portrait_cover[0].path
    ) {
      values["zh_portrait_cover"] = values["zh_portrait_cover"][0]["path"];
    }
    if (
      values.ug_portrait_cover &&
      values.ug_portrait_cover.length > 0 &&
      values.ug_portrait_cover[0].path
    ) {
      values["ug_portrait_cover"] = values["ug_portrait_cover"][0]["path"];
    }

    if (
      values.zh_cover &&
      values.zh_cover.length > 0 &&
      values.zh_cover[0].file_path
    ) {
      values["zh_cover"] = values["zh_cover"][0]["file_path"];
    }
    if (
      values.ug_cover &&
      values.ug_cover.length > 0 &&
      values.ug_cover[0].file_path
    ) {
      values["ug_cover"] = values["ug_cover"][0]["file_path"];
    }
    if (
      values.zh_portrait_cover &&
      values.zh_portrait_cover.length > 0 &&
      values.zh_portrait_cover[0].file_path
    ) {
      values["zh_portrait_cover"] = values["zh_portrait_cover"][0]["file_path"];
    }
    if (
      values.ug_portrait_cover &&
      values.ug_portrait_cover.length > 0 &&
      values.ug_portrait_cover[0].file_path
    ) {
      values["ug_portrait_cover"] = values["ug_portrait_cover"][0]["file_path"];
    }
    
    // Transform commission form data to API format
    if (selectedLevels.length > 0 && values.commissions) {
      const agentLevelCommissions = {};
      selectedLevels.forEach((levelId) => {
        const commissionData = values.commissions[levelId];
        if (commissionData && commissionData.commission !== undefined && commissionData.parent_commission !== undefined) {
          agentLevelCommissions[levelId] = {
            commission: commissionData.commission,
            parent_commission: commissionData.parent_commission,
          };
        }
      });
      if (Object.keys(agentLevelCommissions).length > 0) {
        values["agent_level_commissions"] = agentLevelCommissions;
      }
    }
    // Remove the form commissions structure
    delete values.commissions;

    values.not_for_sale = values.not_for_sale ? 1 : 0;

    setLoading(true);
    const params = {
      data: values,
      token: user.token,
    };

    if (id) {
      updateAlbumMutation.mutate({
        resource: "course/albums",
        params: { ...params, id },
      });
    } else {
      addAlbumMutation.mutate({ resource: "course/albums", params });
    }
  };

  const onFinishFailed = (errorInfo) => {
    setLoading(false);
    showNotificationWithIcon(errorInfo);
  };

  const handleCoursesSelected = (courses) => {
    // Merge with existing courses, avoiding duplicates
    const existingIds = selectedCourses.map((c) => c.id);
    const newCourses = courses.filter((c) => !existingIds.includes(c.id));
    setSelectedCourses([...selectedCourses, ...newCourses]);
    setCourseModalVisible(false);
  };

  const handleRemoveCourse = (courseId) => {
    setSelectedCourses(selectedCourses.filter((c) => c.id !== courseId));
  };

  const calculateTotalPrice = () => {
    return selectedCourses.reduce((sum, course) => sum + (course.price || 0), 0);
  };

  const calculateDiscount = (originalPrice, albumPrice) => {
    if (originalPrice > 0 && albumPrice > 0) {
      return ((originalPrice - albumPrice) / originalPrice * 100).toFixed(0);
    }
    return 0;
  };

  const breadCrumbData = [
    {
      href: "/main/courses/albums",
      title: i18n.t("course.album.title"),
    },
    {
      title: id ? i18n.t("course.album.edit") : i18n.t("course.album.add"),
    },
  ];

  const courseColumns = [
    {
      title: i18n.t("course.courseInfo"),
      render: (item) => {
        return (
          <div className="info">
            <div className="cover">
              <img
                src={i18n.getPropNameByLocale(item, "cover")}
                alt={i18n.getPropNameByLocale(item, "name")}
                width={60}
                height={60}
                style={{ objectFit: "cover" }}
              />
            </div>
            <div className="name">
              <span>{i18n.getPropNameByLocale(item, "name")}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: i18n.t("course.type"),
      render: (item) => {
        return <span>{item.type_label}</span>;
      },
      width: 120,
    },
    {
      title: i18n.t("course.price"),
      render: (item) => {
        return <span>¥{item.price?.toFixed(2) || "0.00"}</span>;
      },
      width: 100,
    },
    {
      title: i18n.t("users.actions"),
      width: 100,
      render: (item) => {
        return (
          <Popconfirm
            title={i18n.t("course.album.confirmRemoveCourse")}
            onConfirm={() => handleRemoveCourse(item.id)}
            okText={i18n.t("base.modal.okText")}
            cancelText={i18n.t("base.modal.cancelText")}
          >
            <Button danger type="link" size="small" icon={<DeleteOutlined />}>
              {i18n.t("course.album.removeCourse")}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  if (isLoading) {
    return <Spin spinning={isLoading} />;
  }

  return (
    <div className="edit-album-page">
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={loading}>
        <Form
          form={albumForm}
          className="album-form"
          name="basic"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Card title={i18n.t("course.basicInfo")} className="basic-info info">
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="ug_name"
                  label={i18n.t("course.ugName")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("productEdit.standardRequired"),
                    },
                  ]}
                >
                  <Input
                    addonAfter={
                      <TranslateBtn
                        context={getFieldValue("ug_name")}
                        onFinish={(value) =>
                          setFieldsValue({
                            zh_name: value,
                          })
                        }
                        from="ug"
                        to="zh"
                      />
                    }
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="zh_name"
                  label={i18n.t("course.zhName")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("productEdit.standardRequired"),
                    },
                  ]}
                >
                  <Input
                    addonAfter={
                      <TranslateBtn
                        context={getFieldValue("zh_name")}
                        onFinish={(value) =>
                          setFieldsValue({
                            ug_name: value,
                          })
                        }
                        from="zh"
                        to="ug"
                      />
                    }
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="course_category_id"
                  label={i18n.t("course.selectCategory")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("productEdit.standardRequired"),
                    },
                  ]}
                >
                  <LinkSelector
                    items={["courseCategories"]}
                    selectedLink={
                      category
                        ? {
                            key: category.id,
                            ug_name: `دەرس تۈرى~${category.ug_name}`,
                            zh_name: `课程分类~${category.zh_name}`,
                            url: `/main/course/category/index?courseId=${category.id}`,
                          }
                        : false
                    }
                    setSelectedLink={(link) => {
                      setCategory({
                        id: link.key,
                        ug_name: link.ug_name.replace("دەرس تۈرى~", ""),
                        zh_name: link.zh_name.replace("课程分类~", ""),
                      });
                      if (!link.key) {
                        setCategory();
                      }
                      setFieldsValue({
                        course_category_id: link.key,
                      });
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>

            {/* Cover Images Row */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue, setFieldsValue }) => (
                    <Form.Item
                      name="zh_cover"
                      label={i18n.t("course.zhCover")}
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      rules={[
                        {
                          required: true,
                          message: i18n.t("productEdit.standardRequired"),
                        },
                      ]}
                    >
                      <ImageUpload
                        images={getFieldValue("zh_cover")}
                        setSelectedImages={(image) => {
                          setFieldsValue({
                            zh_cover: image,
                          });
                        }}
                        aspectRatio="16:9"
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue, setFieldsValue }) => (
                    <Form.Item
                      name="ug_cover"
                      label={i18n.t("course.ugCover")}
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      rules={[
                        {
                          required: true,
                          message: i18n.t("productEdit.standardRequired"),
                        },
                      ]}
                    >
                      <ImageUpload
                        images={getFieldValue("ug_cover")}
                        setSelectedImages={(image) => {
                          setFieldsValue({
                            ug_cover: image,
                          });
                        }}
                        aspectRatio="16:9"
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>

            {/* Portrait Cover Images Row */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue, setFieldsValue }) => (
                    <Form.Item
                      name="zh_portrait_cover"
                      label={i18n.t("course.zhPortraitCover")}
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      rules={[
                        {
                          required: true,
                          message: i18n.t("productEdit.standardRequired"),
                        },
                      ]}
                    >
                      <ImageUpload
                        images={getFieldValue("zh_portrait_cover")}
                        setSelectedImages={(image) => {
                          setFieldsValue({
                            zh_portrait_cover: image,
                          });
                        }}
                        aspectRatio="3:4"
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue, setFieldsValue }) => (
                    <Form.Item
                      name="ug_portrait_cover"
                      label={i18n.t("course.ugPortraitCover")}
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      rules={[
                        {
                          required: true,
                          message: i18n.t("productEdit.standardRequired"),
                        },
                      ]}
                    >
                      <ImageUpload
                        images={getFieldValue("ug_portrait_cover")}
                        setSelectedImages={(image) => {
                          setFieldsValue({
                            ug_portrait_cover: image,
                          });
                        }}
                        aspectRatio="3:4"
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="zh_description"
                  label={i18n.t("course.album.zhDescription")}
                >
                  <SlateRichTextEditor
                    setFieldsValue={(value) =>
                      setFieldsValue({
                        zh_description: value,
                      })
                    }
                    initialValue={id ? data?.zh_description : null}
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="ug_description"
                  label={i18n.t("course.album.ugDescription")}
                >
                  <SlateRichTextEditor
                    setFieldsValue={(value) =>
                      setFieldsValue({
                        ug_description: value,
                      })
                    }
                    initialValue={id ? data?.ug_description : null}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Card>

          <Card title={i18n.t("course.album.pricing")} className="pricing-info info">
            <Form.Item
              name="not_for_sale"
              label={i18n.t("course.notForSale")}
              valuePropName="checked"
              initialValue={false}
            >
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const notForSale = getFieldValue("not_for_sale");
                const originalPrice = getFieldValue("original_price") || 0;
                const albumPrice = getFieldValue("price") || 0;
                const discount = calculateDiscount(originalPrice, albumPrice);
                return (
                  <>
                    <Form.Item
                      name="market_price"
                      label={i18n.t("course.album.marketPrice")}
                    >
                      <InputNumber
                        style={{ width: 200 }}
                        min={0}
                        precision={2}
                        addonAfter={<span>{i18n.t("common.yuan")}</span>}
                      />
                    </Form.Item>

                    <Form.Item
                      name="original_price"
                      label={i18n.t("course.album.originalPrice")}
                    >
                      <InputNumber
                        style={{ width: 200 }}
                        min={0}
                        precision={2}
                        addonAfter={<span>{i18n.t("common.yuan")}</span>}
                      />
                    </Form.Item>

                    <Form.Item
                      name="price"
                      label={i18n.t("course.album.price")}
                      rules={
                        notForSale
                          ? []
                          : [
                              {
                                required: true,
                                message: i18n.t("productEdit.standardRequired"),
                              },
                            ]
                      }
                    >
                      <InputNumber
                        style={{ width: 200 }}
                        min={0}
                        precision={2}
                        addonAfter={<span>{i18n.t("common.yuan")}</span>}
                      />
                    </Form.Item>

                    {!notForSale && discount > 0 ? (
                      <Form.Item label={i18n.t("course.album.discount")}>
                        <span
                          style={{
                            color: "#ff4d4f",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                        >
                          {discount}%
                        </span>
                      </Form.Item>
                    ) : null}
                  </>
                );
              }}
            </Form.Item>
          </Card>

          <Card title={i18n.t("course.album.settings")} className="settings-info info">
            <Form.Item
              name="status"
              label={i18n.t("course.album.status")}
              initialValue={1}
            >
              <Radio.Group>
                <Radio value={1}>{i18n.t("course.album.statusOnShelf")}</Radio>
                <Radio value={0}>{i18n.t("course.album.statusOffShelf")}</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="order"
              label={i18n.t("course.album.order")}
            >
              <InputNumber style={{ width: 120 }} min={0} />
            </Form.Item>

            <Form.Item
              name="valid_forever"
              label={i18n.t("course.validDate")}
              initialValue={1}
            >
              <Radio.Group
                onChange={(v) => {
                  if (v.target.value === 0) {
                    setShowValidDays(true);
                  } else {
                    setShowValidDays(false);
                  }
                }}
              >
                <Radio value={1}>{i18n.t("course.album.validForever")}</Radio>
                <Radio value={0}>{i18n.t("course.album.validDays")}</Radio>
              </Radio.Group>
            </Form.Item>

            {showValidDays ? (
              <Form.Item
                name="valid_days"
                label={i18n.t("course.album.validDays")}
                rules={[
                  {
                    required: true,
                    message: i18n.t("productEdit.standardRequired"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: 200 }}
                  min={1}
                  addonAfter={<span>{i18n.t("common.dayUnit")}</span>}
                />
              </Form.Item>
            ) : null}
          </Card>

          <Card title={i18n.t("course.album.courseManagement")} className="course-management info">
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button
                  type="primary"
                  onClick={() => setCourseModalVisible(true)}
                >
                  {i18n.t("course.album.addCourses")}
                </Button>
                {selectedCourses.length > 0 && (
                  <span>
                    {i18n.t("course.album.coursesCount")}: {selectedCourses.length}
                  </span>
                )}
              </Space>
            </div>

            {selectedCourses.length > 0 ? (
              <>
                <Table
                  className="list"
                  rowKey="id"
                  columns={courseColumns}
                  dataSource={selectedCourses}
                  pagination={false}
                />
                <div style={{ marginTop: 16, padding: "12px", background: "#f5f5f5", borderRadius: "4px" }}>
                  <Space>
                    <span>{i18n.t("course.album.totalCoursesPrice")}:</span>
                    <span style={{ fontWeight: "bold" }}>¥{calculateTotalPrice().toFixed(2)}</span>
                    <span style={{ marginLeft: 16 }}>{i18n.t("course.album.albumPrice")}:</span>
                    <span style={{ fontWeight: "bold", color: "#ff4d4f" }}>
                      ¥{albumForm.getFieldValue("price")?.toFixed(2) || "0.00"}
                    </span>
                  </Space>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                {i18n.t("course.album.noCourses")}
              </div>
            )}
          </Card>

          {merchantHasCommission(user) && (
            <Card title={i18n.t("course.commissionInfo")} className="commission-info info">
              <AlbumCommissionForm
                album={data}
                setCommissions={setCommissions}
                commissions={commissions}
                form={albumForm}
                setSelectedLevels={setSelectedLevels}
              />
            </Card>
          )}

          <Form.Item className="submit">
            <Button type="primary" htmlType="submit">
              {i18n.t("productEdit.saveAndBack")}
            </Button>
          </Form.Item>
        </Form>
      </Spin>

      <CourseSelectionModal
        visible={courseModalVisible}
        onCancel={() => setCourseModalVisible(false)}
        selectedCourseIds={selectedCourses.map((c) => c.id)}
        excludeCourseIds={[]}
        onCoursesSelected={handleCoursesSelected}
      />
    </div>
  );
};

export default AddOrEditAlbum;

